import React from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getAllSalesUsers,
  getAllActiveSalesUsersByProjectId,
  getMyData,
  bulkTransferLeads
} from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import { Users } from 'lucide-react';

/**
 * Bulk assign selected leads to one sales user.
 * Assignee dropdown scoping mirrors UpdateSalesUser (single transfer unchanged).
 */
const BulkTransferSalesUser = ({
  onClose,
  leadIds = [],
  projectId,
  onSuccess
}) => {
  const userId = parseInt(Cookies.get('userId'), 10);
  const userVertical = Cookies.get('userVertical');
  const userLevel = Cookies.get('userDesignationLevel');
  const isSalesVertical = userVertical === 'Sales';
  const isSalesExecutive = isSalesVertical && userLevel === 'Low';
  const isSalesManager = isSalesVertical && userLevel === 'Medium';
  const projectIdNum =
    projectId != null && projectId !== '' ? parseInt(projectId, 10) : null;
  const hasProjectScope = Number.isFinite(projectIdNum) && projectIdNum > 0;
  const selectedCount = Array.isArray(leadIds) ? leadIds.length : 0;

  const { register, handleSubmit } = useForm({
    defaultValues: {
      sales_id: ''
    }
  });

  const { data: salesUsers } = useQuery({
    queryKey: ['assignableSalesUsers', hasProjectScope ? projectIdNum : 'all'],
    queryFn: () =>
      hasProjectScope
        ? getAllActiveSalesUsersByProjectId(projectIdNum)
        : getAllSalesUsers(1, 1000)
  });

  const { data: meFull } = useQuery({
    queryKey: ['meFullForLeadAssign'],
    queryFn: () => getMyData({ full: true }),
    enabled: isSalesManager
  });

  const assignableUsers = React.useMemo(() => {
    const list = (salesUsers?.data || []).filter((user) => user?.is_active);
    const byId = new Map(list.map((user) => [user.id, user]));

    const ensureUnique = (users) => {
      const seen = new Set();
      return users.filter((user) => {
        if (!user?.id || seen.has(user.id)) return false;
        seen.add(user.id);
        return true;
      });
    };

    if (isSalesExecutive) {
      return ensureUnique(list.filter((user) => user.level === 'Low'));
    }

    if (isSalesManager) {
      const managerEntries = meFull?.manager || [];
      const forProject = hasProjectScope
        ? managerEntries.filter((m) => Number(m.project_id) === projectIdNum)
        : managerEntries;

      const reportees = forProject
        .map((m) => {
          const nested = m.user;
          if (nested?.id) {
            return byId.get(nested.id) || nested;
          }
          if (m.user_id) {
            return byId.get(m.user_id) || null;
          }
          return null;
        })
        .filter((user) => user && user.is_active !== false);

      const self = byId.get(userId) || meFull || null;
      const scoped = ensureUnique([self, ...reportees].filter(Boolean));

      if (scoped.length > 1) {
        return scoped;
      }

      return ensureUnique(
        [
          self,
          ...list.filter((user) => user.level === 'Low' && user.id !== userId)
        ].filter(Boolean)
      );
    }

    return ensureUnique(list);
  }, [
    salesUsers?.data,
    meFull,
    userId,
    isSalesExecutive,
    isSalesManager,
    hasProjectScope,
    projectIdNum
  ]);

  const { mutate: runBulkTransfer, isPending } = useMutation({
    mutationFn: (data) =>
      bulkTransferLeads({
        lead_ids: leadIds,
        sales_id: parseInt(data.sales_id, 10)
      }),
    onSuccess: (result) => {
      const transferred = Number(result?.transferred) || 0;
      const failed = Number(result?.failed) || 0;
      if (transferred > 0 && failed === 0) {
        toast.success(`Transferred ${transferred} lead${transferred === 1 ? '' : 's'}`);
      } else if (transferred > 0) {
        toast.success(`Transferred ${transferred}, ${failed} failed`);
      } else {
        toast.error(result?.results?.[0]?.error || 'No leads were transferred');
      }
      onSuccess?.(result);
      onClose?.();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Bulk transfer failed'
      );
    }
  });

  const onSubmit = (data) => {
    if (!data.sales_id) {
      toast.error('Select a sales user');
      return;
    }
    if (!selectedCount) {
      toast.error('Select at least one lead');
      return;
    }
    runBulkTransfer(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="rounded-lg border border-teal-100 bg-teal-50/70 px-3 py-2.5">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-700/10 text-teal-800">
            <Users size={16} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-teal-950">
              Transfer {selectedCount} selected lead{selectedCount === 1 ? '' : 's'}
            </p>
            <p className="mt-0.5 text-xs text-teal-900/80">
              Assigns all selected leads to one person. Each lead gets a transfer log showing who
              transferred and to whom.
            </p>
          </div>
        </div>
      </div>

      <FormInput
        width={100}
        label="Assign to"
        register={register}
        register_key="sales_id"
        type="dropdown"
        options={[
          { value: '', label: 'Select sales user', disabled: true },
          ...assignableUsers.map((user) => ({
            value: user.id.toString(),
            label: user.name
          }))
        ]}
      />

      <div className="flex justify-end items-center space-x-4 mt-2">
        <Button type="button" onClick={onClose} displayType="secondary" disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending || selectedCount === 0} isLoading={isPending}>
          Transfer
        </Button>
      </div>
    </form>
  );
};

export default BulkTransferSalesUser;
